import { useFlag } from "@unleash/nextjs";
import { useAtomValue } from "jotai";
import intersection from "lodash/intersection";
import { useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { useArticleRenderContext } from "~/components/article/article-render/contexts";
import { useArticleSpeechUtils } from "~/components/article/article-speech/article-speech-player/hooks";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { useArticleSpeechConfig } from "~/lib/app/hooks/scmp-article-speech-config";
import { rosettaAtom } from "~/lib/rosetta";
import type { hooksShowGenericInlineWidgetByArticleArticle$key } from "~/queries/__generated__/hooksShowGenericInlineWidgetByArticleArticle.graphql";

import { HidePlayerBySectionEntityIds, HidePlayerByTypeEntityIds } from "./consts";

export const useShowGenericInlineWidgetByArticle = (
  reference: hooksShowGenericInlineWidgetByArticleArticle$key,
) => {
  const article = useFragment(
    graphql`
      fragment hooksShowGenericInlineWidgetByArticleArticle on Article {
        ...helpersCheckIsPostiesArticle
        ...hooksPaywallPromiseContent
        entityId
        sections {
          value {
            entityId
          }
        }
        types {
          value {
            entityId
          }
        }
        topics {
          entityId
        }
      }
    `,
    reference,
  );

  const isSCMPArticleSpeechEnabled = useFlag("enable_scmp_article_speech");

  const sanitizedSectionEntityIds = useMemo(
    () =>
      article.sections?.flatMap(
        section => section?.value?.map(element => element?.entityId ?? "") ?? [],
      ) ?? [],
    [article.sections],
  );

  const sanitizedTypeEntityIds = useMemo(
    () =>
      article.types?.flatMap(type => type?.value?.map(element => element?.entityId ?? "") ?? []) ??
      [],
    [article.types],
  );

  const sanitizedTopicEntityIds = useMemo(
    () => article.topics?.map(topic => topic?.entityId) ?? [],
    [article.topics],
  );

  const isHideSCMPArticleSpeechRequired = useMemo(() => {
    if (!isSCMPArticleSpeechEnabled) return true;

    const doesArticleHaveHideSectionEntityId =
      intersection(HidePlayerBySectionEntityIds, sanitizedSectionEntityIds).length > 0;

    const doesArticleHaveHideTypeEntityId =
      intersection(HidePlayerByTypeEntityIds, sanitizedTypeEntityIds).length > 0;

    return doesArticleHaveHideSectionEntityId || doesArticleHaveHideTypeEntityId;
  }, [isSCMPArticleSpeechEnabled, sanitizedSectionEntityIds, sanitizedTypeEntityIds]);

  const { scmpArticleSpeechConfigState } = useArticleSpeechConfig();

  const isForceHideSCMPArticleSpeechWidget = useMemo(() => {
    if (!scmpArticleSpeechConfigState) return false;

    const hasArticleEntityId = (
      scmpArticleSpeechConfigState.forceHideByArticleEntityId ?? []
    ).includes(article.entityId);

    const hasSectionEntityId =
      intersection(
        scmpArticleSpeechConfigState.forceHideBySectionEntityId ?? [],
        sanitizedSectionEntityIds,
      ).length > 0;

    const hasTypeEntityId =
      intersection(
        scmpArticleSpeechConfigState.forceHideByTypeEntityId ?? [],
        sanitizedTypeEntityIds,
      ).length > 0;

    const hasTopicEntityId =
      intersection(
        scmpArticleSpeechConfigState.forceHideByTopicEntityId ?? [],
        sanitizedTopicEntityIds,
      ).length > 0;

    return hasArticleEntityId || hasSectionEntityId || hasTypeEntityId || hasTopicEntityId;
  }, [
    article.entityId,
    sanitizedSectionEntityIds,
    sanitizedTopicEntityIds,
    sanitizedTypeEntityIds,
    scmpArticleSpeechConfigState,
  ]);

  return {
    isWidgetVisibleByType: !isHideSCMPArticleSpeechRequired && !isForceHideSCMPArticleSpeechWidget,
  };
};

export const useEnableGenericInlineWidgetByUser = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const hasIpAccess =
    asyncRosettaState?.result?.instance?.rulesEngine.getFact("hasIpAccess") ?? false;

  const isWidgetEnabledByUser = useMemo(() => {
    if (!asyncRosettaState?.result?.instance) return;

    /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
    return (
      asyncRosettaState?.result?.user?.isCorpSubscriber ||
      asyncRosettaState?.result?.user?.isAccessGrantedUser ||
      asyncRosettaState?.result?.user?.isFreeSubscriber ||
      asyncRosettaState?.result?.user?.isInternalStaff ||
      asyncRosettaState?.result?.isScmpSubscriber ||
      asyncRosettaState?.result?.user?.isSiteLicenseSubscriber ||
      hasIpAccess
    );
    /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
  }, [asyncRosettaState?.result, hasIpAccess]);

  return {
    isWidgetEnabledByUser,
  };
};

export const useAutoCollapseEffect = () => {
  const { percentageReached, setPercentageReached } = useArticleRenderContext();
  const { articleSpeechifyPlayer } = useArticleSpeechContext();
  const { articleElementReference } = useArticleRenderContext();
  const { checkIsCurrentArticlePlaying } = useArticleSpeechUtils();

  // This effect is for resetting the percentageReached when the article is playing
  useEffect(() => {
    if (!checkIsCurrentArticlePlaying(articleElementReference?.current) || !articleSpeechifyPlayer)
      return;

    const removeEventListener = articleSpeechifyPlayer.addEventListener(event => {
      switch (event.type) {
        case "play":
          setPercentageReached?.(undefined);
          return;
      }
    });
    return () => removeEventListener();
  }, [
    articleElementReference,
    articleSpeechifyPlayer,
    checkIsCurrentArticlePlaying,
    setPercentageReached,
  ]);

  useEffect(() => {
    if (!checkIsCurrentArticlePlaying(articleElementReference?.current) || !percentageReached)
      return;
    articleSpeechifyPlayer?.minimizeWidget();
  }, [
    articleElementReference,
    articleSpeechifyPlayer,
    checkIsCurrentArticlePlaying,
    percentageReached,
  ]);
};
